import { ChevronLeft, ChevronRight } from "lucide-react";
import type React from "react";
import { type MouseEventHandler, useEffect, useRef } from "react";
import {
	type ArrowProps,
	type DotProps as LibDotProps,
	type ButtonGroupProps,
} from "react-multi-carousel";

import { cn } from "@/lib/utils";

/** Frosted pill behind carousel dot track — same glass as chevrons (no hover). */
export const mediaCarouselChromeGlass =
	"rounded-full border border-white/55 bg-white/25 shadow-[0_4px_20px_rgba(0,0,0,0.08)] backdrop-blur-md drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)] dark:border-white/20 dark:bg-black/25 dark:shadow-[0_4px_20px_rgba(0,0,0,0.35)] dark:backdrop-blur-lg";

/** Frosted chevron / enlarge controls — [mediaCarouselChromeGlass] + hover + white glyphs. */
export const mediaCarouselArrowGlass = cn(
	mediaCarouselChromeGlass,
	"text-white transition-[background-color,border-color,box-shadow,opacity,color] duration-200 hover:border-white/75 hover:bg-white/40 hover:text-white hover:shadow-[0_6px_24px_rgba(0,0,0,0.1)] dark:!text-white dark:hover:border-white/40 dark:hover:bg-black/35 dark:hover:!text-white",
);

type DotProps = Omit<LibDotProps, "onClick"> & { onClick: MouseEventHandler<HTMLButtonElement> };

function activateCarouselArrow(
	e: React.SyntheticEvent,
	disabled: boolean,
	onClick: ArrowProps["onClick"],
) {
	e.stopPropagation();
	if (disabled) return;
	onClick?.call(undefined);
}

export function ArrowButtonLeft({
	onClick,
	disabled,
	className,
	"aria-label": ariaLabel = "Previous slide",
}: Pick<ArrowProps, "onClick"> & {
	disabled: boolean;
	className?: string;
	"aria-label"?: string;
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			aria-label={ariaLabel}
			data-carousel-control=""
			onClick={(e) => activateCarouselArrow(e, disabled, onClick)}
			className={cn(
				"size-8 touch-manipulation cursor-pointer text-white disabled:opacity-50",
				className,
			)}
		>
			<ChevronLeft className="pointer-events-none size-8" aria-hidden />
		</button>
	);
}

export function ArrowButtonRight({
	onClick,
	disabled,
	className,
	"aria-label": ariaLabel = "Next slide",
}: Pick<ArrowProps, "onClick"> & {
	disabled: boolean;
	className?: string;
	"aria-label"?: string;
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			aria-label={ariaLabel}
			data-carousel-control=""
			onClick={(e) => activateCarouselArrow(e, disabled, onClick)}
			className={cn(
				"size-8 touch-manipulation cursor-pointer text-white disabled:opacity-50",
				className,
			)}
		>
			<ChevronRight className="pointer-events-none size-8" aria-hidden />
		</button>
	);
}

export function CustomDotWeak(props: DotProps) {
	return (
		<button
			aria-label={`Go to ${props.index} slide`}
			onClick={props.onClick}
			className={cn("block size-1.5 rounded-full bg-white/40 disabled:opacity-50 sm:size-2", {
				"bg-white": props.active,
			})}
		/>
	);
}

export function CustomDot({
	index,
	active,
	disabled,
	onClick,
}: Required<Omit<DotProps, "carouselState">> & { disabled: boolean }) {
	return (
		<button
			disabled={disabled}
			aria-label={`Go to ${index} slide`}
			onClick={onClick}
			className={cn("block size-1.5 rounded-full bg-white/45 disabled:opacity-50 sm:size-2", {
				"bg-white": active,
			})}
		/>
	);
}

type ArbitraryButtonGroupProps = {
	itemLength: number;
	infinite?: boolean;
	/** Merged onto the dots row wrapper (e.g. opacity / pointer-events from MediaCarousel) */
	chromeClassName?: string;
	/** When > 0, call library `next` on this interval (ms). Uses the same `next` as arrow controls. */
	autoPlayMs?: number;
	/** Lets the parent drive autoplay with the same `next` inject as arrows. */
	bindCarouselNext?: (next: (() => void) | null) => void;
	onUserNavigate?: () => void;
} & ButtonGroupProps;

function DotList({
	itemLength,
	disabled,
	carouselState,
	goToSlide,
	infinite = false,
}: ArbitraryButtonGroupProps & { disabled: boolean }) {
	if (carouselState === undefined) return null;

	/**
	 * Note(Curstantine):
	 * When the carousel is infinitely moving, the list is padded by 2.
	 * So the currentSlide state is always + 2
	 */
	const { currentSlide: cx } = carouselState;
	const currentSlide = infinite ? cx - 2 : cx;

	/**
	 * Note(Curstantine):
	 * The carousel implementation "clones" the items by a certain multiplier to increase the perception of the list.
	 *
	 * To pinpoint the clone's parent (in a scenario with totalItems=12, itemLength=4, current=6)
	 * ```
	 * const sequenceIndex = current / itemLength; // 1.5 floored to 1
	 * const index = (current - (sequenceIndex * itemLength)) // (6 - (1 * 4)) = 2
	 * ```
	 */
	const cloneSequenceIndex = Math.floor(currentSlide / itemLength);
	const standardIndex = currentSlide - cloneSequenceIndex * itemLength;

	const onClick = (i: number) => {
		goToSlide?.call(undefined, currentSlide + (i - standardIndex));
	};

	return (
		<ul className="inline-flex max-w-full flex-wrap items-center justify-center gap-0.5 px-0.5 leading-none sm:gap-1 sm:px-1">
			{new Array(itemLength).fill(null).map((_, i) => (
				<li key={i} className="flex items-center">
					<CustomDot
						index={i}
						active={i === standardIndex}
						disabled={disabled}
						onClick={(e) => {
							e.stopPropagation();
							onClick(i);
						}}
					/>
				</li>
			))}
		</ul>
	);
}

export function CustomButtonGroup({
	next,
	previous,
	carouselState,
	goToSlide,
	itemLength,
	infinite,
	showArrows = true,
	chromeClassName,
	autoPlayMs = 0,
	bindCarouselNext,
	onUserNavigate,
}: ArbitraryButtonGroupProps & { showArrows?: boolean }) {
	const allShown = carouselState?.totalItems === itemLength;
	const localShowArrows = showArrows && itemLength > 1;
	const nextRef = useRef(next);
	nextRef.current = next;

	useEffect(() => {
		if (!bindCarouselNext) return;
		bindCarouselNext(() => {
			nextRef.current?.();
		});
		return () => bindCarouselNext(null);
	}, [bindCarouselNext]);

	useEffect(() => {
		if (!autoPlayMs || autoPlayMs <= 0 || itemLength <= 1) return;
		const id = window.setInterval(() => {
			// Prefer `next` — `goToSlide` has an extra isInThrottle guard that can no-op.
			nextRef.current?.();
		}, autoPlayMs);
		return () => window.clearInterval(id);
	}, [autoPlayMs, itemLength]);

	if (itemLength <= 1 && !localShowArrows) {
		return null;
	}

	return (
		<div
			className={cn(
				"pointer-events-none absolute inset-x-0 bottom-2 z-30 flex items-center justify-center gap-3 px-2",
				chromeClassName,
			)}
		>
			{localShowArrows && (
				<span className="pointer-events-auto">
					<ArrowButtonLeft
						disabled={allShown}
						onClick={() => {
							onUserNavigate?.();
							previous?.();
						}}
					/>
				</span>
			)}
			{itemLength > 1 && (
				<div
					className={cn(
						"pointer-events-auto inline-flex max-w-[calc(100%-5.5rem)] items-center px-1 py-1 sm:py-px",
						mediaCarouselChromeGlass,
					)}
				>
					<DotList
						disabled={allShown}
						itemLength={itemLength}
						carouselState={carouselState}
						goToSlide={
							goToSlide
								? (slide, skip) => {
										onUserNavigate?.();
										goToSlide(slide, skip);
									}
								: undefined
						}
						infinite={infinite}
					/>
				</div>
			)}
			{localShowArrows && (
				<span className="pointer-events-auto">
					<ArrowButtonRight
						disabled={allShown}
						onClick={() => {
							onUserNavigate?.();
							next?.();
						}}
					/>
				</span>
			)}
		</div>
	);
}
